import type {
  SelfserviceShortcutCategory,
  SelfserviceShortcutLink
} from 'de-zurich-ui-library'
import { getIdLevel } from '@/utils/KeycloakUtils'

export type ShortcutTranslate = (key: string) => string

export interface ShortcutLinkDefinition {
  href?: string
  labelKey: string
  trackingId: string
  visible?: boolean
}

const createShortcutLink = (
  definition: ShortcutLinkDefinition,
  index: number,
  translate: ShortcutTranslate
): SelfserviceShortcutLink | null => {
  const href = definition.href?.trim()
  if (!href || definition.visible === false) return null

  const isExternal = /^(?:https?:)?\/\//i.test(href)

  return {
    ariaLabel: translate(definition.labelKey),
    href,
    index,
    label: translate(definition.labelKey),
    target: isExternal ? '_blank' : undefined,
    trackingId: definition.trackingId
  }
}

export const createShortcutCategory = (
  id: string,
  titleKey: string,
  definitions: ShortcutLinkDefinition[],
  translate: ShortcutTranslate
): SelfserviceShortcutCategory => ({
  id,
  links: definitions
    .map((definition, index) => createShortcutLink(definition, index, translate))
    .filter((link): link is SelfserviceShortcutLink => link !== null),
  title: translate(titleKey),
  trackingId: `overview-selfservice-shortcut-${id}`
})

export const createPersonalShortcutCategory = (
  translate: ShortcutTranslate
): SelfserviceShortcutCategory => {
  const personalLinks = globalThis.ENV_CONFIG?.SERVICE_LINKS.PERSONAL_PAGE
  const settingsLinks = globalThis.ENV_CONFIG?.SERVICE_LINKS.SETTINGS_PAGE
  const showActivationCodeServices = getIdLevel() === 0

  return createShortcutCategory(
    'personal-profile',
    'overviewSelfserviceShortcut.categories.personalProfile',
    [
      {
        href: personalLinks?.CHANGE_NAME_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeName',
        trackingId: 'shortcut-change-name'
      },
      {
        href: personalLinks?.CHANGE_ADDRESS_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeAddress',
        trackingId: 'shortcut-change-address'
      },
      {
        href: personalLinks?.CHANGE_BANKDATA_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeBankData',
        trackingId: 'shortcut-change-bank-data'
      },
      {
        href: settingsLinks?.CHANGE_EMAIL_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeEmail',
        trackingId: 'shortcut-change-email'
      },
      {
        href: settingsLinks?.CHANGE_USERNAME_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeUsername',
        trackingId: 'shortcut-change-username'
      },
      {
        href: settingsLinks?.CHANGE_PASSWORD_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changePassword',
        trackingId: 'shortcut-change-password'
      },
      {
        href: personalLinks?.CHANGE_ADVERTISING_CONSENT_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.changeAdvertisingConsent',
        trackingId: 'shortcut-change-advertising-consent'
      },
      {
        href: settingsLinks?.CHANGE_SERVICECODEREQUEST_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.requestActivationCode',
        trackingId: 'shortcut-request-activation-code',
        visible: showActivationCodeServices
      },
      {
        href: settingsLinks?.CHANGE_SERVICECODEINPUT_PAGE,
        labelKey: 'overviewSelfserviceShortcut.links.enterActivationCode',
        trackingId: 'shortcut-enter-activation-code',
        visible: showActivationCodeServices
      }
    ],
    translate
  )
}
